import { useState, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { extractTextFromPdf, type ExtractedPdf } from '@/lib/pdf/extractor'
import {
  extractEntitiesWithLLM,
  extractEntitiesFromText,
  generateInstanceUri,
  getLLMLogs,
  exportLLMLogs,
  type ExtractedEntity,
} from '@/lib/pdf/entityExtractor'
import { update } from '@/lib/api/sparql-adapter'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAIStore } from '@/stores/aiStore'
import { NAMESPACES } from '@/lib/ontology/namespaces'
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  ChevronDown,
  ChevronRight,
  Trash2,
  Edit2,
  Save,
  Sparkles,
  Settings,
  Download,
} from 'lucide-react'

type ProcessingStep = 'upload' | 'extract' | 'map' | 'import' | 'complete'

interface PdfProcessorProps {
  onNavigateToSettings?: () => void
}

export function PdfProcessor({ onNavigateToSettings }: PdfProcessorProps) {
  const { isConnected } = useConnectionStore()
  const { apiKey, isConfigured, documentAnalysisModel } = useAIStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<ProcessingStep>('upload')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [extractedPdf, setExtractedPdf] = useState<ExtractedPdf | null>(null)
  const [entities, setEntities] = useState<ExtractedEntity[]>([])
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set())
  const [editingEntity, setEditingEntity] = useState<string | null>(null)
  const [editedNames, setEditedNames] = useState<Record<string, string>>({})

  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setLoading(true)
    setError(null)
    setStep('extract')

    try {
      // Step 1: Extract text from PDF
      const pdf = await extractTextFromPdf(file)
      setExtractedPdf(pdf)

      // Step 2: Extract entities using LLM or fallback
      let result
      if (isConfigured && apiKey) {
        try {
          result = await extractEntitiesWithLLM(pdf.fullText, apiKey, documentAnalysisModel)
        } catch (llmError) {
          console.error('LLM extraction failed, falling back to rule-based:', llmError)
          setError('AI extraction failed. Using basic extraction instead.')
          result = extractEntitiesFromText(pdf.fullText)
        }
      } else {
        // Fallback to rule-based extraction
        result = extractEntitiesFromText(pdf.fullText)
      }

      setEntities(result.entities)

      // Pre-select high-confidence entities
      const highConfidence = result.entities
        .filter((e) => e.confidence === 'high')
        .map((e) => e.id)
      setSelectedEntities(new Set(highConfidence))

      setStep('map')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process PDF')
      setStep('upload')
    } finally {
      setLoading(false)
    }
  }

  const toggleEntity = (id: string) => {
    const newSelected = new Set(selectedEntities)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedEntities(newSelected)
  }

  const selectAll = () => {
    setSelectedEntities(new Set(entities.map((e) => e.id)))
  }

  const selectNone = () => {
    setSelectedEntities(new Set())
  }

  const startEditing = (id: string, currentName: string) => {
    setEditingEntity(id)
    setEditedNames((prev) => ({ ...prev, [id]: currentName }))
  }

  const saveEdit = (id: string) => {
    setEntities((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, text: editedNames[id] || e.text } : e
      )
    )
    setEditingEntity(null)
  }

  const removeEntity = (id: string) => {
    setEntities((prev) => prev.filter((e) => e.id !== id))
    setSelectedEntities((prev) => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })
  }

  const handleImport = async () => {
    if (!isConnected) {
      setError('Please connect to Fuseki first')
      return
    }

    setLoading(true)
    setError(null)
    setStep('import')

    const entitiesToImport = entities.filter((e) => selectedEntities.has(e.id))
    let success = 0
    let failed = 0

    for (const entity of entitiesToImport) {
      try {
        const instanceUri = generateInstanceUri(entity.suggestedClass.localName, entity.text)

        const sparql = `
          PREFIX rdf: <${NAMESPACES.rdf}>
          PREFIX rdfs: <${NAMESPACES.rdfs}>
          PREFIX io: <${NAMESPACES.io}>

          INSERT DATA {
            <${instanceUri}> rdf:type <${entity.suggestedClass.uri}> .
            <${instanceUri}> rdfs:label "${entity.text.replace(/"/g, '\\"')}" .
            <${instanceUri}> rdfs:comment "${entity.context.replace(/"/g, '\\"').slice(0, 500)}" .
          }
        `

        await update(sparql)
        success++
      } catch {
        failed++
      }
    }

    setImportResult({ success, failed })
    setStep('complete')
    setLoading(false)
  }

  const reset = () => {
    setStep('upload')
    setExtractedPdf(null)
    setEntities([])
    setSelectedEntities(new Set())
    setEditedNames({})
    setImportResult(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const confidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return 'bg-business/20 text-business'
      case 'medium':
        return 'bg-market/20 text-market'
      case 'low':
        return 'bg-solution/20 text-solution'
    }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                PDF to Ontology Mapper
              </CardTitle>
              <CardDescription>
                Upload a PDF document, extract entities with AI, and map them to ontology classes
              </CardDescription>
            </div>

            {/* AI Status Badge & Log Export */}
            <div className="flex items-center gap-2">
              {isConfigured ? (
                <Badge className="bg-business/20 text-business flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI Enabled
                </Badge>
              ) : (
                <Badge variant="outline" className="flex items-center gap-1">
                  Basic Mode
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={exportLLMLogs}
                title={`Export LLM logs (${getLLMLogs().length} entries)`}
              >
                <Download className="w-4 h-4 mr-1" />
                Logs
              </Button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {(['upload', 'extract', 'map', 'import', 'complete'] as ProcessingStep[]).map(
              (s, i) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === s
                        ? 'bg-primary text-white'
                        : ['upload', 'extract', 'map', 'import', 'complete'].indexOf(step) > i
                          ? 'bg-business text-white'
                          : 'bg-background text-text-muted'
                    }`}
                  >
                    {i + 1}
                  </div>
                  {i < 4 && (
                    <div
                      className={`w-8 h-0.5 ${
                        ['upload', 'extract', 'map', 'import', 'complete'].indexOf(step) > i
                          ? 'bg-business'
                          : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              )
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto">
          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-solution/10 text-solution">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div>
              {!isConfigured && (
                <div className="mb-4 p-3 bg-market/10 border border-market/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-market">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">AI not configured</span>
                    </div>
                    {onNavigateToSettings && (
                      <Button variant="outline" size="sm" onClick={onNavigateToSettings}>
                        <Settings className="w-4 h-4 mr-1" />
                        Settings
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-text-muted mt-1">
                    Configure your Claude API key in Settings to enable AI-powered semantic extraction.
                    Without it, only basic pattern matching will be used.
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <Upload className="w-12 h-12 mx-auto text-text-muted mb-4" />
                <p className="text-lg font-medium text-text mb-2">
                  Click to select a PDF file
                </p>
                <p className="text-sm text-text-muted">
                  {isConfigured
                    ? 'The PDF will be analyzed by Claude AI to extract business entities'
                    : 'The PDF will be processed with basic pattern matching'}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Extracting */}
          {step === 'extract' && loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <p className="text-lg font-medium">
                {isConfigured ? 'Analyzing with Claude AI...' : 'Processing PDF...'}
              </p>
              <p className="text-sm text-text-muted">
                {isConfigured
                  ? 'Extracting and inferring business entities from document'
                  : 'Extracting text and identifying entities'}
              </p>
            </div>
          )}

          {/* Step 3: Map Entities */}
          {step === 'map' && extractedPdf && (
            <div className="space-y-4">
              {/* PDF Info */}
              <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">{extractedPdf.fileName}</p>
                    <p className="text-xs text-text-muted">
                      {extractedPdf.totalPages} pages • {entities.length} entities found
                      {isConfigured && <span className="ml-1 text-business">(AI-extracted)</span>}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={reset}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>

              {/* Selection Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectNone}>
                    Select None
                  </Button>
                </div>
                <p className="text-sm text-text-muted">
                  {selectedEntities.size} of {entities.length} selected
                </p>
              </div>

              {/* Entity List */}
              {entities.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  No entities could be extracted from this PDF.
                  {!isConfigured && (
                    <p className="mt-2 text-sm">
                      Try configuring Claude AI in Settings for better results.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {entities.map((entity) => (
                    <EntityCard
                      key={entity.id}
                      entity={entity}
                      isSelected={selectedEntities.has(entity.id)}
                      isEditing={editingEntity === entity.id}
                      editedName={editedNames[entity.id]}
                      onToggle={() => toggleEntity(entity.id)}
                      onStartEdit={() => startEditing(entity.id, entity.text)}
                      onSaveEdit={() => saveEdit(entity.id)}
                      onEditChange={(name) =>
                        setEditedNames((prev) => ({ ...prev, [entity.id]: name }))
                      }
                      onRemove={() => removeEntity(entity.id)}
                      confidenceColor={confidenceColor}
                    />
                  ))}
                </div>
              )}

              {/* Import Button */}
              <div className="pt-4 border-t border-border">
                {!isConnected && (
                  <p className="text-sm text-solution mb-2">
                    Connect to Fuseki in Settings to import entities
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={handleImport}
                  disabled={selectedEntities.size === 0 || !isConnected}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import {selectedEntities.size} Entities
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {step === 'import' && loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <p className="text-lg font-medium">Importing entities...</p>
              <p className="text-sm text-text-muted">Creating instances in the triplestore</p>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 'complete' && importResult && (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="w-16 h-16 text-business mb-4" />
              <p className="text-xl font-medium mb-2">Import Complete!</p>
              <div className="flex gap-4 text-sm">
                <span className="text-business">{importResult.success} imported</span>
                {importResult.failed > 0 && (
                  <span className="text-solution">{importResult.failed} failed</span>
                )}
              </div>
              <Button className="mt-6" onClick={reset}>
                Process Another PDF
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Entity Card Component
interface EntityCardProps {
  entity: ExtractedEntity
  isSelected: boolean
  isEditing: boolean
  editedName?: string
  onToggle: () => void
  onStartEdit: () => void
  onSaveEdit: () => void
  onEditChange: (name: string) => void
  onRemove: () => void
  confidenceColor: (c: 'high' | 'medium' | 'low') => string
}

function EntityCard({
  entity,
  isSelected,
  isEditing,
  editedName,
  onToggle,
  onStartEdit,
  onSaveEdit,
  onEditChange,
  onRemove,
  confidenceColor,
}: EntityCardProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={`border rounded-lg p-3 transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="mt-1 w-4 h-4 rounded border-border"
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isEditing ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => onEditChange(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-border rounded"
                autoFocus
              />
            ) : (
              <span className="font-medium truncate">{entity.text}</span>
            )}
            <Badge variant={entity.domain} className="text-xs">
              {entity.suggestedClass.localName}
            </Badge>
            <Badge className={`text-xs ${confidenceColor(entity.confidence)}`}>
              {entity.confidence}
            </Badge>
          </div>

          {/* Context Toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-text"
          >
            {expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            {expanded ? 'Hide context' : 'Show context'}
          </button>

          {/* Expanded Context */}
          {expanded && (
            <p className="mt-2 text-xs text-text-muted bg-background p-2 rounded">
              {entity.context}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {isEditing ? (
            <Button variant="ghost" size="icon" onClick={onSaveEdit}>
              <Save className="w-4 h-4" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={onStartEdit}>
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onRemove}>
            <Trash2 className="w-4 h-4 text-solution" />
          </Button>
        </div>
      </div>
    </div>
  )
}
